import React, { useState, useEffect, useMemo } from 'react';
import { HIGHER_ED_FEEDS } from './constants/feeds';
import { fetchAndParseFeed } from './utils/rssParser';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ArticleCard from './components/ArticleCard';
import Loader from './components/Loader';
import SafeIcon from './common/SafeIcon';
import { FiAlertCircle, FiCheckCircle, FiXCircle, FiTrendingUp, FiBookOpen } from 'react-icons/fi';

// Updated to 2 days (48 hours)
const TWO_DAYS_AGO = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);

function App() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedStatus, setFeedStatus] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  const [showStatus, setShowStatus] = useState(false);

  const loadFeeds = async () => {
    setLoading(true);
    setFeedStatus([]);
    setArticles([]);

    const BATCH_SIZE = 4;
    let allArticles = [];
    const statuses = [];

    for (let i = 0; i < HIGHER_ED_FEEDS.length; i += BATCH_SIZE) {
      const batch = HIGHER_ED_FEEDS.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(batch.map((feed) => fetchAndParseFeed(feed)));

      results.forEach((result, idx) => {
        const feed = batch[idx];
        if (result.status === 'fulfilled' && result.value.length > 0) {
          // Filter for articles from the last 2 days
          const recent = result.value.filter((a) => a.pubDate >= TWO_DAYS_AGO);
          allArticles = [...allArticles, ...recent];
          statuses.push({ name: feed.name, count: recent.length, ok: true });
        } else {
          statuses.push({ name: feed.name, count: 0, ok: false });
        }
      });

      const sorted = [...allArticles].sort((a, b) => b.pubDate - a.pubDate);
      setArticles(sorted);
      setFeedStatus([...statuses]);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadFeeds();
  }, []);

  const filteredArticles = useMemo(() => {
    if (activeFilter === 'All') return articles;
    return articles.filter((a) => a.sourceName === activeFilter);
  }, [articles, activeFilter]);

  const successCount = feedStatus.filter((s) => s.ok).length;

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      <Header onRefresh={loadFeeds} isRefreshing={loading} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {feedStatus.length > 0 && (
          <div className="mb-8 bg-black rounded-xl shadow-lg p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-[#FF0000] p-1.5 rounded-full">
                  <SafeIcon icon={FiCheckCircle} className="text-white text-sm" />
                </div>
                <div>
                  <p className="text-xs font-black text-white uppercase tracking-widest">
                    {successCount} / {feedStatus.length} Sources Online
                  </p>
                  <p className="text-[10px] text-gray-400 font-bold">
                    WINDOW: LAST 2 CALENDAR DAYS
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowStatus((p) => !p)}
                className="text-[10px] text-[#FF0000] hover:text-white font-black uppercase tracking-tighter transition-colors border-b border-[#FF0000]/30"
              >
                {showStatus ? 'Close Logs' : 'View Logs'}
              </button>
            </div>
            {showStatus && (
              <div className="mt-5 pt-5 border-t border-gray-800 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {feedStatus.map((s) => (
                  <div
                    key={s.name}
                    className={`flex items-center gap-2 text-[10px] px-2.5 py-1.5 rounded border ${
                      s.ok
                        ? 'bg-gray-900 border-gray-800 text-gray-300'
                        : 'bg-red-950/30 border-red-900 text-red-400'
                    }`}
                  >
                    <div
                      className={`w-1.5 h-1.5 rounded-full ${
                        s.ok ? 'bg-green-500' : 'bg-red-600 animate-pulse'
                      }`}
                    ></div>
                    <span className="truncate font-bold uppercase">{s.name}</span>
                    {s.ok && <span className="ml-auto text-white font-black">{s.count}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-10">
          <aside className="lg:w-72 flex-shrink-0">
            <Sidebar feeds={HIGHER_ED_FEEDS} activeFilter={activeFilter} onFilterChange={setActiveFilter} />

            <div className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200">
              <h4 className="text-xs font-black text-black uppercase tracking-widest mb-2 flex items-center gap-2">
                <SafeIcon icon={FiTrendingUp} className="text-[#FF0000]" />
                About this Feed
              </h4>
              <p className="text-xs text-gray-500 leading-relaxed">
                This personalized dashboard aggregates real-time updates from premier higher education news sources, curated specifically for Dr. Lionel Maten's daily review.
              </p>
            </div>
          </aside>

          <div className="flex-1">
            {loading && articles.length === 0 ? (
              <Loader />
            ) : filteredArticles.length > 0 ? (
              <>
                <div className="mb-8 flex items-end justify-between border-b-2 border-black pb-4">
                  <div>
                    <h2 className="text-3xl font-black text-black uppercase tracking-tighter">
                      {activeFilter === 'All' ? 'The Daily Brief' : activeFilter}
                    </h2>
                    <p className="text-xs font-bold text-[#FF0000] uppercase tracking-[0.3em] mt-1">
                      Insights from the last 48 hours
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-4xl font-black text-black block leading-none">
                      {filteredArticles.length}
                    </span>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                      Articles Found
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {filteredArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </>
            ) : !loading ? (
              <div className="bg-white rounded-xl border-4 border-dashed border-gray-100 p-20 text-center">
                <SafeIcon icon={FiAlertCircle} className="text-5xl text-gray-200 mx-auto mb-4" />
                <h3 className="text-xl font-black text-black uppercase mb-2">No Recent Updates</h3>
                <p className="text-gray-400 text-sm max-w-xs mx-auto">
                  There are no articles from the last 2 days for this specific source.
                </p>
                {activeFilter !== 'All' && (
                  <button
                    onClick={() => setActiveFilter('All')}
                    className="mt-6 px-6 py-2 bg-black text-white text-xs font-black uppercase tracking-widest hover:bg-[#FF0000] transition-colors rounded"
                  >
                    Return to All Sources
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </main>

      <footer className="bg-black text-white py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="inline-block bg-[#FF0000] p-2 rounded mb-4">
            <SafeIcon icon={FiBookOpen} className="text-2xl" />
          </div>
          <h2 className="text-xl font-black uppercase tracking-tighter mb-2">Dr. Lionel Maten</h2>
          <p className="text-gray-500 text-xs tracking-[0.2em] uppercase mb-8">Higher Education Leadership & Strategy</p>
          <div className="flex justify-center gap-8 text-xs font-bold uppercase tracking-widest mb-8">
            <a href="https://dr.lionelmaten.com/" className="hover:text-[#FF0000] transition-colors">Website</a>
            <a href="https://zcal.co/ligonroadllc/DrLM" className="hover:text-[#FF0000] transition-colors">Schedule</a>
            <a href="#" className="text-gray-700 cursor-not-allowed">Privacy</a>
          </div>
          <p className="text-gray-700 text-[10px]">© {new Date().getFullYear()} Dr. Lionel Maten. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;